import { NgModule, Injector } from '@angular/core';
import { RootModule, UIRouterModule, Transition, UIRouter, StatesModule, TransitionService, UrlRuleHandlerFn, UrlParts, TargetStateDef, StateService } from '@uirouter/angular';
import { HttpResponse } from '@angular/common/http';
import * as moment from 'moment';
import { BusyIndicatorService } from './busy-indicator/busy-indicator.service';
import { NotificationService } from './notification/notification.service';
import { NotificationType } from './notification/notification-type.enum';
import { FmApiService } from './FMAPI/fm-api.service';
import { GetShowRequest } from './FMAPI/get-show-request';
import { GetShowResponse } from './FMAPI/get-show-response';
import { UserPreferenceService } from './user-preference/user-preference.service';
import { ShowComponent } from './show/show.component';
import { ShowMasterComponent } from './show-master/show-master.component';
import { ShowMasterDetailComponent } from './show-master-detail/show-master-detail.component';
import { ShowNewspaperComponent } from './show-newspaper/show-newspaper.component';
import { FavoriteEditComponent } from './favorite-edit/favorite-edit.component';

const rootModule: RootModule = {
  states: [
    {
      name: "show",
      url: "/show",
      component: ShowComponent
    },
    {
      name: "show.master",
      url: "/masterdetail",
      component: ShowMasterComponent,
      resolve: [
        {
          token: "ResolveChannels",
          deps: [FmApiService, NotificationService],
          resolveFn: (api: FmApiService, notify: NotificationService) => api
            .getChannels()
            .toPromise()
            .catch(error => {
              let desc = JSON.stringify(error);
              notify.Notify("Failed to get channel list.", NotificationType.Danger);
              throw error;
            })
        }
      ]
    },
    {
      name: "show.masterdetail",
      url: "/masterdetail/:chcode?showdate",
      component: ShowMasterDetailComponent,
      resolve: [
        {
          token: "GetShowResponse",
          deps: [Transition, FmApiService, NotificationService],
          resolveFn: (transition: Transition, api: FmApiService, notify: NotificationService) => {
            const request = new GetShowRequest();
            request.ChannelNumbers = [Number(transition.params().chcode)];

            const dateStr = transition.params().showdate;
            if (!dateStr) {
              let startDate = moment().startOf('day').toISOString();
              request.DateStart = startDate;
            } else {
              let startDate = moment(this.showDate).toISOString();
              request.DateStart = startDate;
            }
            let endDate = moment(request.DateStart).add(1, "days").toISOString();
            request.DateEnd = endDate;

            return api
              .getShow(request)
              .toPromise()
              .catch(error => {
                let desc = JSON.stringify(error);
                notify.Notify("Failed to get show list.", NotificationType.Danger)
                throw error;
              });
          }
        }
      ]
    },
    {
      name: "show.newspaper",
      url: "/newspaper",
      redirectTo: (transition: Transition) => {
        let userprefsvc: UserPreferenceService = transition.injector().get(UserPreferenceService);
        let userpref = userprefsvc.Get();
        let target = transition.router.stateService.target(
          "show.newspaperfavorite",
          { favoriteid: userpref.SelectedFavorite });
        return target;
      }
    },
    {
      name: "show.newspaperfavorite",
      url: "/newspaper/:favoriteid?showdate",
      component: ShowNewspaperComponent,
      resolve: [
        {
          token: "GetShowResponse",
          deps: [Transition, StateService, UserPreferenceService, FmApiService, NotificationService],
          resolveFn: (transition: Transition, state: StateService, userPrefSvc: UserPreferenceService, api: FmApiService, notification: NotificationService) => {
            const favoriteid = Number(transition.params().favoriteid);
            const userpref = userPrefSvc.Get();

            if (!userpref.Favorites[favoriteid]) {
              state.go(state.current, { favoriteid: 0 }, { inherit: true });
              return;
            }

            const favorite = userpref.Favorites[favoriteid];

            const request = new GetShowRequest();
            request.ChannelNumbers = favorite.Channels.map(ch => Number(ch.Code));

            const dateStr = transition.params().showdate;
            if (!dateStr) {
              let startDate = moment().startOf('day').toISOString();
              request.DateStart = startDate;
            } else {
              let startDate = moment(this.showDate).toISOString();
              request.DateStart = startDate;
            }
            let endDate = moment(request.DateStart).add(1, "days").toISOString();
            request.DateEnd = endDate;

            return api
              .getShow(request)
              .toPromise()
              .catch(error => {
                let desc = JSON.stringify(error);
                notification.Notify("Failed to get show list.", NotificationType.Danger)
                state.go("show.master");
                throw error;
              });
          }
        }
      ]
    },
    {
      name: "favoriteEdit",
      url: "/favorite/:favoriteid/edit",
      component: FavoriteEditComponent,
      resolve: [
        {
          token: "Channels",
          deps: [FmApiService, NotificationService],
          resolveFn: (api: FmApiService, notify: NotificationService) => api
            .getChannels()
            .toPromise()
            .catch(error => {
              let desc = JSON.stringify(error);
              notify.Notify("Failed to get channel list.", NotificationType.Danger);
              throw error;
            })
        }
      ]
    }
  ],
  config: (uiRouter: UIRouter, injector: Injector, statesModule: StatesModule) => {
    let trans = uiRouter.transitionService;
    let busy = injector.get(BusyIndicatorService);
    SetBusyIndicator(trans, busy);

    let userPrefSvc: UserPreferenceService = injector.get(UserPreferenceService);
    SetOtherwise(uiRouter, userPrefSvc);

    // uiRouter.stateService.defaultErrorHandler(error => error);
  },
  useHash: true
};

export function SetBusyIndicator(transition: TransitionService, busy: BusyIndicatorService) {
  transition.onStart({}, (transition) => { busy.Start() });
  transition.onSuccess({}, (t) => busy.Finish());
  transition.onError({}, t => busy.Finish());
}

export function SetOtherwise(uiRouter: UIRouter, userPrefSvc: UserPreferenceService) {
  let handler: UrlRuleHandlerFn = (matchValue: any, url: UrlParts, router: UIRouter) => {
    let userPref = userPrefSvc.Get();

    let targetString = "show.";
    if (userPref.SelectedView == "master" || userPref.SelectedView == "newspaper") {
      targetString += userPref.SelectedView;
    } else {
      targetString += "master";
    }

    let targetState = router.stateRegistry.matcher.find(targetString);
    if (!!targetState) {
      router.stateService.go(targetString, null, { reload: true });
    } else {
      return "/show/masterdetail";
    }
  };

  uiRouter.urlService.rules.otherwise(handler);
}

@NgModule({
  imports: [UIRouterModule.forRoot(rootModule)],
  exports: [UIRouterModule],
  providers: [
  ]
})
export class AppRoutingModule { }



// WEBPACK FOOTER //
// D:/OTHERS/FirstmediaShowSchedule/src/app/app-routing.module.ts